import { UM } from '../_components';
import type { Building } from './types';

// ─── Styles ──────────────────────────────────────────────────────────────────

const CARD_STYLE: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: 8,
  minHeight: 74,
};

const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 14,
  paddingBottom: 4,
  borderBottom: '1px solid #d1d5db',
  marginBottom: 8,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function QualityBadge({ quality }: { quality: 'Statistics' | 'OpenData' | 'User' }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    Statistics: { bg: '#fef3c7', text: '#92400e', label: 'Stat' },
    OpenData: { bg: '#dbeafe', text: '#1e40af', label: 'OD' },
    User: { bg: '#dcfce7', text: '#166534', label: 'User' },
  };
  const s = styles[quality];
  return (
    <span style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 600, background: s.bg, color: s.text, lineHeight: 1, display: 'inline-block' }}>
      {s.label}
    </span>
  );
}

const PURPOSE_LABELS: Record<string, string> = {
  Commerce: 'Commerce', CollectiveHousing: 'Logement collectif', Offices: 'Bureaux',
  Education: 'Enseignement', ShoppingMall: 'Centre commercial', ClinicHospital: 'Hôpital/Clinique',
  IndividualHousing: 'Logement individuel', IndustryAndAgriculture: 'Industrie',
};

function energyForHeatingType(type: string): string {
  const map: Record<string, string> = { Gas: 'Gaz', Electric: 'Électricité', DistrictHeating: 'Réseau' };
  return map[type] || 'Gaz';
}

// ─── Main component ──────────────────────────────────────────────────────────

interface BuildingSummaryPanelProps {
  building: Building;
}

export function BuildingSummaryPanel({ building }: BuildingSummaryPanelProps) {
  return (
    <aside style={{
      width: 300, minWidth: 300, height: '100%', background: UM.bg,
      borderLeft: `1px solid ${UM.border}`, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', zIndex: 20,
    }}>
      {/* ─── Header ─── */}
      <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${UM.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{
            background: UM.badgeBlue, color: UM.badgeBlueText, padding: '4px 12px',
            borderRadius: 16, fontSize: 13, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 14 }}>🏢</span> {building.id}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" aria-label="Panel étendu ouvert" style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${UM.border}`, background: '#e2e8f0', cursor: 'pointer', fontSize: 14 }} title="Panel étendu ouvert">⊟</button>
            <button type="button" aria-label="Éditer" style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${UM.border}`, background: UM.bgHeader, cursor: 'pointer', fontSize: 14 }} title="Éditer">✏️</button>
          </div>
        </div>

        <h3 style={{ margin: '12px 0 4px', fontSize: 16, fontWeight: 700, color: UM.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {building.name}
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: UM.textMuted, lineHeight: 1.4 }}>
          {building.address}, {building.postalCode} {building.city}
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${UM.border}`, background: UM.bg, fontSize: 12, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <span>🛰</span> Street View
          </button>
          <button style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${UM.border}`, background: UM.bg, fontSize: 12, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <span>🌍</span> Earth
          </button>
        </div>
      </div>

      {/* ─── Prospection selector ─── */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${UM.border}`, flexShrink: 0 }}>
        <select style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, color: '#374151' }}>
          <option>Ajouter à une sélection...</option>
          <option>RC Brétigny-sur-Orge (12 bât.)</option>
          <option>Extension Massy (8 bât.)</option>
        </select>
      </div>

      {/* ─── Scrollable content ─── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── Section: Énergies & Consommations ── */}
        <div style={SECTION_TITLE_STYLE}>Énergies &amp; Consommations</div>

        {/* Total chaud card */}
        <div style={{ ...CARD_STYLE, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Total chaud</span>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: UM.text }}>{(building.heatConsumption / 1000).toFixed(0)}</div>
              <div style={{ fontSize: 11, color: UM.textMuted }}>MWhu</div>
            </div>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />
          {/* Chauffage */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
            <div>
              <div style={{ fontSize: 12, color: UM.textMuted, marginBottom: 2 }}>Chauffage</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{energyForHeatingType(building.heatingType)}</span>
                <QualityBadge quality="OpenData" />
                <span style={{ fontWeight: 500 }}>Collectif</span>
                <QualityBadge quality="Statistics" />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{(building.heatConsumption * 0.7 / 1000).toFixed(0)}</div>
              <div style={{ fontSize: 11, color: UM.textMuted }}>MWhu</div>
            </div>
          </div>
          {/* ECS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
            <div>
              <div style={{ fontSize: 12, color: UM.textMuted, marginBottom: 2 }}>Eau Chaude Sanitaire</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{energyForHeatingType(building.hotWaterType)}</span>
                <QualityBadge quality="OpenData" />
                <span style={{ fontWeight: 500 }}>Collectif</span>
                <QualityBadge quality="Statistics" />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{(building.heatConsumption * 0.3 / 1000).toFixed(0)}</div>
              <div style={{ fontSize: 11, color: UM.textMuted }}>MWhu</div>
            </div>
          </div>
        </div>

        {/* Froid card */}
        <div style={{ ...CARD_STYLE, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, color: UM.textMuted, marginBottom: 2 }}>Climatisation / Froid</div>
              {building.coldConsumption > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <span style={{ fontWeight: 500 }}>Réseau</span>
                  <QualityBadge quality="OpenData" />
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{building.coldConsumption > 0 ? (building.coldConsumption / 1000).toFixed(0) : '--'}</div>
              <div style={{ fontSize: 11, color: UM.textMuted }}>MWhu</div>
            </div>
          </div>
        </div>

        {/* ── Section: Caractéristiques ── */}
        <div style={SECTION_TITLE_STYLE}>Caractéristiques</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {/* Usages - span 2 */}
          <div style={{ ...CARD_STYLE, gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>Usages</span>
              <QualityBadge quality="OpenData" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>
              {PURPOSE_LABELS[building.purpose] || building.purpose} (65%)
            </div>
            {building.secondaryPurpose && (
              <div style={{ fontSize: 12, color: UM.textMuted, marginTop: 2 }}>
                {building.secondaryPurpose} (35%)
              </div>
            )}
          </div>

          {/* Surface */}
          <div style={CARD_STYLE}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>Surface totale</span>
              <QualityBadge quality="OpenData" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{building.totalArea.toLocaleString()} m²</div>
          </div>

          {/* Storeys */}
          <div style={CARD_STYLE}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>Nombre de niveaux</span>
              <QualityBadge quality="OpenData" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{building.storeys}</div>
          </div>

          {/* Construction year */}
          <div style={CARD_STYLE}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>Année de construction</span>
              <QualityBadge quality="OpenData" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{building.constructionYear}</div>
          </div>

          {/* DPE */}
          <div style={CARD_STYLE}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>DPE</span>
              <QualityBadge quality="OpenData" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{building.dpe}</div>
          </div>

          {/* Owner - span 2 */}
          <div style={{ ...CARD_STYLE, gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 11, color: UM.textMuted }}>Propriétaire</span>
              <QualityBadge quality="User" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>
              {building.publicPrivate === 'Public' ? 'Commune de Brétigny-sur-Orge' : 'Foncia, CAAP IMMO INVEST'}
            </div>
            <div style={{ fontSize: 12, color: UM.textMuted, marginTop: 2 }}>
              {building.publicPrivate === 'Public' ? 'Public' : 'Privé'}
            </div>
          </div>

          {/* Last modification - span 2 */}
          <div style={{ ...CARD_STYLE, gridColumn: 'span 2', background: '#f9fafb' }}>
            <div style={{ fontSize: 11, color: UM.textMuted }}>Dernière modification</div>
            <div style={{ fontSize: 12, marginTop: 4, color: '#374151' }}>18/06/2026 par <strong>Martin Maurey</strong></div>
          </div>
        </div>
      </div>
    </aside>
  );
}
